import { useState, useRef, useEffect, useCallback } from 'react';
import DataTable from './DataTable';
import { theme } from '../theme';

const TYPE_LABELS = { whatsapp: 'WhatsApp', messenger: 'Messenger', instagram: 'Instagram', web: 'Canal Web' };

const COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'number', label: 'Número' },
  { key: 'type', label: 'Tipo', render: (row) => TYPE_LABELS[row.type] || row.type },
  { key: 'type_handler', label: 'Tipo Manejador' },
  { key: 'id_manejador', label: 'ID Manejador' },
  { key: 'created_at', label: 'Creado' },
  { key: 'updated_at', label: 'Actualizado' },
];

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  backgroundColor: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '7px',
  color: theme.colors.text,
  fontSize: '0.95rem',
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  color: theme.colors.textMuted,
  marginBottom: '0.4rem',
  fontSize: '0.85rem',
};

const META_SCOPES = {
  messenger: 'pages_show_list,pages_messaging,pages_manage_metadata,business_management',
  instagram: 'pages_show_list,pages_messaging,pages_manage_metadata,business_management,instagram_basic,instagram_manage_messages',
};

// Carga el SDK de Meta desde sus servidores (requerido por Meta — no se puede auto-alojar)
function loadFbSdk(appId) {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      window.FB.init({ appId, cookie: true, xfbml: false, version: 'v19.0' });
      resolve(window.FB);
      return;
    }
    const timeout = setTimeout(() => reject(new Error('Tiempo de carga del SDK de Meta agotado')), 15000);
    window.fbAsyncInit = function () {
      clearTimeout(timeout);
      window.FB.init({ appId, cookie: true, xfbml: false, version: 'v19.0' });
      resolve(window.FB);
    };
    const script = document.createElement('script');
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    script.async = true;
    script.defer = true;
    script.onerror = () => { clearTimeout(timeout); reject(new Error('No se pudo cargar el SDK de Meta')); };
    document.head.appendChild(script);
  });
}

// PASO 0 — selección de tipo de canal
function ChannelTypePicker({ onPick, onClose }) {
  const opts = [
    { type: 'whatsapp', label: 'WhatsApp', desc: 'Línea de WhatsApp Business (Embedded Signup).' },
    { type: 'messenger', label: 'Messenger', desc: 'Página de Facebook con Messenger.' },
    { type: 'instagram', label: 'Instagram', desc: 'Cuenta de Instagram vinculada a una página.' },
  ];
  return (
    <div>
      <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginBottom: '1.2rem' }}>
        ¿Qué tipo de canal quieres conectar?
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.2rem' }}>
        {opts.map((o) => (
          <button key={o.type} onClick={() => onPick(o.type)}
            style={{ textAlign: 'left', padding: '0.9rem 1rem', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', cursor: 'pointer' }}>
            <div style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '0.95rem' }}>{o.label}</div>
            <div style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>{o.desc}</div>
          </button>
        ))}
      </div>
      <button onClick={onClose} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>
        Cancelar
      </button>
    </div>
  );
}

// PASO 1 — pantalla de inicio del onboarding
function OnboardingIntro({ onStart, onClose, loading, error }) {
  return (
    <div>
      <p style={{ color: theme.colors.text, fontSize: '0.92rem', lineHeight: '1.7', marginBottom: '1.2rem' }}>
        Este proceso conectará una <strong style={{ color: theme.colors.primary }}>línea nueva</strong> (SIM card propia,
        sin WABA existente) a tu empresa a través del proceso oficial de Meta.
      </p>
      <ul style={{ color: theme.colors.textMuted, fontSize: '0.87rem', lineHeight: '1.8', paddingLeft: '1.2rem', marginBottom: '1.5rem' }}>
        <li>Se abrirá una ventana de Meta para crear o asociar tu WABA.</li>
        <li>Registrarás el número de WhatsApp Business.</li>
        <li>Al finalizar, completa los datos del canal aquí.</li>
      </ul>
      {error && (
        <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{error}</p>
      )}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>
          Cancelar
        </button>
        <button onClick={onStart} disabled={loading} style={{ flex: 1, padding: '10px', backgroundColor: loading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Iniciando Meta...' : 'Iniciar con Meta'}
        </button>
      </div>
    </div>
  );
}

// PASO Messenger/IG — conectar con Facebook
function ConnectMetaIntro({ type, onConnect, onClose, loading, error }) {
  const label = type === 'instagram' ? 'Instagram' : 'Messenger';
  return (
    <div>
      <p style={{ color: theme.colors.text, fontSize: '0.92rem', lineHeight: 1.7, marginBottom: '1rem' }}>
        Conecta tu cuenta de <strong style={{ color: theme.colors.primary }}>{label}</strong>. Se abrirá Facebook para
        autorizar y elegir la página.
      </p>
      {error && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cancelar</button>
        <button onClick={onConnect} disabled={loading} style={{ flex: 1, padding: '10px', backgroundColor: loading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Conectando...' : 'Conectar con Facebook'}
        </button>
      </div>
    </div>
  );
}

// PASO Messenger/IG — elegir página
function SelectPage({ pages, onSelect, onClose, loading, error }) {
  return (
    <div>
      <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginBottom: '1rem' }}>Elige la cuenta a conectar:</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.2rem', maxHeight: '40vh', overflowY: 'auto' }}>
        {pages.map((p) => (
          <button key={p.page_id} disabled={loading} onClick={() => onSelect(p.page_id)}
            style={{ textAlign: 'left', padding: '0.8rem 1rem', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', cursor: loading ? 'not-allowed' : 'pointer' }}>
            <div style={{ color: theme.colors.text, fontWeight: 600, fontSize: '0.92rem' }}>{p.name}</div>
            {p.ig_username && <div style={{ color: theme.colors.textMuted, fontSize: '0.8rem' }}>@{p.ig_username}</div>}
          </button>
        ))}
      </div>
      {error && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{error}</p>}
      <button onClick={onClose} disabled={loading} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cancelar</button>
    </div>
  );
}

// PASO final — configurar manejador (canales creados vía onboarding server-side)
function HandlerStep({ form, setForm, onSubmit, onClose, loading, error, flows, campaigns, connectedName }) {
  const handlerOptions = form.type_handler === 'campaign' ? campaigns : flows;
  return (
    <div>
      <div style={{ backgroundColor: 'rgba(74,222,128,0.08)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.2rem', fontSize: '0.85rem', color: theme.colors.success }}>
        ✓ Cuenta conectada: <strong>{connectedName}</strong>
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <label style={labelStyle}>Tipo de Manejador</label>
        <select value={form.type_handler} onChange={(e) => setForm((f) => ({ ...f, type_handler: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="flow">Flow</option>
          <option value="campaign">Campaign</option>
        </select>
      </div>
      <div style={{ marginBottom: '1.5rem' }}>
        <label style={labelStyle}>ID Manejador <span style={{ color: theme.colors.textMuted }}>(opcional)</span></label>
        <select value={form.id_manejador} onChange={(e) => setForm((f) => ({ ...f, id_manejador: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="">— Seleccionar {form.type_handler === 'campaign' ? 'campaña' : 'flujo'} —</option>
          {handlerOptions.map((o) => <option key={o.id} value={o.id}>{o.name} (#{o.id})</option>)}
        </select>
      </div>
      {error && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cerrar</button>
        <button onClick={onSubmit} disabled={loading} style={{ flex: 1, padding: '10px', backgroundColor: loading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer' }}>
          {loading ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

// PASO 0 — elegir Canal Web u otros canales
function ChannelKindPicker({ onPickWeb, onPickOthers, onClose }) {
  const card = { textAlign: 'left', padding: '0.9rem 1rem', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '8px', cursor: 'pointer' };
  return (
    <div>
      <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem', marginBottom: '1.2rem' }}>¿Qué quieres crear?</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.2rem' }}>
        <button onClick={onPickWeb} style={card}>
          <div style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '0.95rem' }}>Canal Web</div>
          <div style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>Widget de chat embebible para tu página web o tus vistas públicas.</div>
        </button>
        <button onClick={onPickOthers} style={card}>
          <div style={{ color: theme.colors.primary, fontWeight: 700, fontSize: '0.95rem' }}>Otros canales</div>
          <div style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>WhatsApp, Messenger o Instagram (Meta).</div>
        </button>
      </div>
      <button onClick={onClose} style={{ width: '100%', padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Cancelar</button>
    </div>
  );
}

// Editor reutilizable de los campos que se le piden al cliente para iniciar el chat.
// (El celular se pide siempre de forma automática, no hace falta agregarlo aquí.)
function WebFieldsEditor({ fields, setFields }) {
  const add = () => setFields((f) => [...f, { label: '', required: true, type: 'text' }]);
  const update = (i, patch) => setFields((f) => f.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const remove = (i) => setFields((f) => f.filter((_, j) => j !== i));
  return (
    <div>
      <label style={labelStyle}>Campos que se le piden al cliente</label>
      <div style={{ fontSize: '0.78rem', color: theme.colors.textMuted, marginBottom: '0.6rem' }}>
        El campo <strong>Celular</strong> se pide siempre automáticamente. Agrega los demás (ej: Nombre, Cédula).
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.7rem' }}>
        {fields.map((f, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
            <input value={f.label} onChange={(e) => update(i, { label: e.target.value })} placeholder="Nombre del campo" style={{ ...inputStyle, flex: 1 }} />
            <select value={f.type} onChange={(e) => update(i, { type: e.target.value })} style={{ ...inputStyle, width: 'auto', cursor: 'pointer' }}>
              <option value="text">Texto</option>
              <option value="number">Número</option>
              <option value="email">Email</option>
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem', color: theme.colors.textMuted, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={!!f.required} onChange={(e) => update(i, { required: e.target.checked })} /> Obl.
            </label>
            <button onClick={() => remove(i)} title="Eliminar" style={{ background: 'none', border: 'none', color: theme.colors.error, cursor: 'pointer', fontSize: '1.1rem' }}>×</button>
          </div>
        ))}
      </div>
      <button onClick={add} style={{ padding: '7px 14px', backgroundColor: 'transparent', border: `1px dashed ${theme.colors.primary}`, borderRadius: '7px', color: theme.colors.primary, fontSize: '0.85rem', cursor: 'pointer' }}>
        + Agregar campo
      </button>
    </div>
  );
}

// Bloque de código con botón copiar (para el snippet embebible).
function CopyBlock({ label, code }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
        <label style={{ ...labelStyle, marginBottom: 0 }}>{label}</label>
        <button onClick={copy} style={{ padding: '3px 10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, borderRadius: '6px', color: theme.colors.primary, fontSize: '0.75rem', cursor: 'pointer' }}>
          {copied ? '¡Copiado!' : 'Copiar'}
        </button>
      </div>
      <pre style={{ margin: 0, padding: '0.7rem', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.text, fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all', overflowX: 'auto' }}>
        {code}
      </pre>
    </div>
  );
}

function Channels({ token }) {
  const tableRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Listas para el dropdown de "ID Manejador"
  const [flows, setFlows] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  useEffect(() => {
    if (!token) return;
    fetch('/api/flows', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((res) => setFlows(res.data || []))
      .catch(() => {});
    fetch('/api/campaigns', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((res) => setCampaigns(res.data || []))
      .catch(() => {});
  }, [token]);

  // --- Estado modal crear ---
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState('kind'); // kind | type | intro | connectMeta | selectPage | handler | web
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [waCode, setWaCode] = useState('');
  const waCodeRef = useRef('');
  const [createForm, setCreateForm] = useState({ number: '', url: '', type: 'whatsapp', type_handler: 'flow', id_manejador: '' });
  const [webFields, setWebFields] = useState([]);   // campos del formulario del canal web (crear)
  const [webWelcome, setWebWelcome] = useState(''); // mensaje de bienvenida opcional
  const [metaPages, setMetaPages] = useState([]);
  const [metaSessionId, setMetaSessionId] = useState('');
  const [connectedChannel, setConnectedChannel] = useState(null); // { channel_id, name }

  // --- Estado modal editar ---
  const [editOpen, setEditOpen] = useState(false);
  const [editChannel, setEditChannel] = useState(null);
  const [editForm, setEditForm] = useState({ type_handler: 'flow', id_manejador: '' });
  const [editWebFields, setEditWebFields] = useState([]);
  const [editWebWelcome, setEditWebWelcome] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');

  // Listener para mensajes del popup de Meta
  const handleMetaMessage = useCallback((event) => {
    const origins = ['https://www.facebook.com', 'https://web.facebook.com'];
    if (!origins.includes(event.origin)) return;
    try {
      const data = JSON.parse(event.data);
      if (data.type !== 'WA_EMBEDDED_SIGNUP') return;

      window.removeEventListener('message', handleMetaMessage);

      if (data.event === 'FINISH') {
        const { phone_number_id, waba_id } = data.data;
        (async () => {
          try {
            const res = await fetch('/api/channels/meta/whatsapp', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ code: waCodeRef.current, waba_id, phone_id: phone_number_id }),
            });
            const d = await res.json();
            if (!res.ok) { setCreateError(d.message || 'No se pudo crear el canal de WhatsApp'); setCreateLoading(false); return; }
            setConnectedChannel({ channel_id: d.channel_id, name: d.number });
            setCreateForm((f) => ({ ...f, number: d.number }));
            setCreateLoading(false);
            setCreateStep('handler');
          } catch { setCreateError('Error de conexión'); setCreateLoading(false); }
        })();
      } else if (data.event === 'CANCEL') {
        setCreateLoading(false);
        setCreateError('El proceso de Meta fue cancelado.');
      } else if (data.event === 'ERROR') {
        setCreateLoading(false);
        setCreateError('Error en el proceso de Meta. Intenta de nuevo.');
      }
    } catch (_) {}
  }, [token]);

  useEffect(() => {
    return () => window.removeEventListener('message', handleMetaMessage);
  }, [handleMetaMessage]);

  // Inicia el onboarding con Meta
  const handleStartMeta = async () => {
    setCreateError('');
    setCreateLoading(true);
    try {
      const configRes = await fetch('/api/channels/meta-config', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!configRes.ok) {
        const d = await configRes.json().catch(() => ({}));
        setCreateError(d.message || 'No se pudo obtener la configuración de Meta');
        setCreateLoading(false);
        return;
      }
      const { app_id } = await configRes.json();

      const FB = await loadFbSdk(app_id);

      window.addEventListener('message', handleMetaMessage);

      FB.login(
        (response) => {
          if (response.authResponse?.code) {
            waCodeRef.current = response.authResponse.code;
            setWaCode(response.authResponse.code);
          } else if (!response.authResponse) {
            window.removeEventListener('message', handleMetaMessage);
            setCreateLoading(false);
            setCreateError('Autorización de Meta cancelada o fallida.');
          }
        },
        {
          scope: 'whatsapp_business_management,whatsapp_business_messaging,business_management',
          extras: { feature: 'whatsapp_embedded_signup', setup: {}, sessionInfoVersion: '3', response_type: 'code' },
        }
      );
    } catch (err) {
      setCreateLoading(false);
      setCreateError(err.message || 'Error al iniciar el proceso de Meta');
    }
  };

  // Abre modal editar
  const openEdit = (channel) => {
    setEditChannel(channel);
    setEditForm({ type_handler: channel.type_handler || 'flow', id_manejador: channel.id_manejador ?? '' });
    // Cargar configuración del formulario si es canal web.
    let cfg = channel.web_config;
    if (typeof cfg === 'string') { try { cfg = JSON.parse(cfg); } catch { cfg = null; } }
    setEditWebFields(Array.isArray(cfg?.fields) ? cfg.fields.map((f) => ({ label: f.label || f.key || '', required: !!f.required, type: f.type || 'text' })) : []);
    setEditWebWelcome(cfg?.welcome || '');
    setEditError('');
    setEditOpen(true);
  };

  // Guarda edición
  const handleEditSubmit = async () => {
    setEditError('');
    setEditLoading(true);
    try {
      const body = {
        type_handler: editForm.type_handler,
        id_manejador: editForm.id_manejador !== '' ? Number(editForm.id_manejador) : null,
      };
      if (editChannel.type === 'web') {
        const cleanFields = editWebFields
          .map((f) => ({ label: (f.label || '').trim(), key: (f.label || '').trim(), required: !!f.required, type: f.type || 'text' }))
          .filter((f) => f.label);
        body.web_config = { fields: cleanFields, welcome: editWebWelcome.trim() };
      }
      const res = await fetch(`/api/channels/${editChannel.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) { setEditError(data.message || 'Error al actualizar'); return; }
      setEditOpen(false);
      showSuccess('Canal actualizado.');
      tableRef.current?.reload();
    } catch {
      setEditError('Error de conexión');
    } finally {
      setEditLoading(false);
    }
  };

  const handleConnectMeta = async () => {
    const type = createForm.type;
    setCreateError(''); setCreateLoading(true);
    try {
      const configRes = await fetch('/api/channels/meta-config', { headers: { Authorization: `Bearer ${token}` } });
      if (!configRes.ok) {
        const d = await configRes.json().catch(() => ({}));
        setCreateError(d.message || 'No se pudo obtener la configuración de Meta');
        setCreateLoading(false);
        return;
      }
      const { app_id } = await configRes.json();
      const FB = await loadFbSdk(app_id);
      FB.login(async (response) => {
        const userToken = response.authResponse?.accessToken;
        if (!userToken) { setCreateLoading(false); setCreateError('Autorización de Facebook cancelada.'); return; }
        try {
          const res = await fetch('/api/channels/meta/pages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ type, userToken }),
          });
          const data = await res.json();
          if (!res.ok) { setCreateError(data.message || 'No se pudieron listar las páginas'); setCreateLoading(false); return; }
          setMetaSessionId(data.session_id);
          setMetaPages(data.pages);
          setCreateStep('selectPage');
        } catch { setCreateError('Error de conexión'); }
        finally { setCreateLoading(false); }
      }, { scope: META_SCOPES[type] });
    } catch (err) {
      setCreateLoading(false);
      setCreateError(err.message || 'Error al iniciar sesión con Facebook');
    }
  };

  const handleSelectPage = async (page_id) => {
    setCreateError(''); setCreateLoading(true);
    try {
      const res = await fetch('/api/channels/meta/connect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: metaSessionId, type: createForm.type, page_id }),
      });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.message || 'No se pudo conectar la página'); setCreateLoading(false); return; }
      setConnectedChannel({ channel_id: data.channel_id, name: data.name });
      setCreateForm((f) => ({ ...f, number: data.name }));
      setCreateStep('handler');
    } catch { setCreateError('Error de conexión'); }
    finally { setCreateLoading(false); }
  };

  const handleConfigureHandler = async () => {
    setCreateError(''); setCreateLoading(true);
    try {
      const res = await fetch(`/api/channels/${connectedChannel.channel_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          type_handler: createForm.type_handler,
          id_manejador: createForm.id_manejador ? Number(createForm.id_manejador) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.message || 'Error al configurar el manejador'); return; }
      closeCreateModal();
      showSuccess('Canal conectado y configurado.');
      tableRef.current?.reload();
    } catch { setCreateError('Error de conexión'); }
    finally { setCreateLoading(false); }
  };

  const handlePickType = (type) => {
    setCreateForm((f) => ({ ...f, type }));
    setCreateError('');
    if (type === 'whatsapp') {
      setCreateStep('intro');           // pantalla de WhatsApp existente
    } else {
      setCreateStep('connectMeta');     // Messenger/Instagram
    }
  };

  // Crea el canal web (POST directo; no hay onboarding de Meta).
  const handleCreateWeb = async () => {
    setCreateError('');
    if (!createForm.number.trim()) { setCreateError('Ponle un nombre al canal.'); return; }
    if (!createForm.id_manejador) { setCreateError('Selecciona el flujo/campaña que manejará el canal.'); return; }
    const cleanFields = webFields
      .map((f) => ({ label: (f.label || '').trim(), key: (f.label || '').trim(), required: !!f.required, type: f.type || 'text' }))
      .filter((f) => f.label);
    setCreateLoading(true);
    try {
      const res = await fetch('/api/channels', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          number: createForm.number.trim(),
          type: 'web',
          type_handler: createForm.type_handler,
          id_manejador: Number(createForm.id_manejador),
          web_config: { fields: cleanFields, welcome: webWelcome.trim() },
        }),
      });
      const data = await res.json();
      if (!res.ok) { setCreateError(data.message || 'No se pudo crear el canal web'); return; }
      closeCreateModal();
      showSuccess('Canal web creado.');
      tableRef.current?.reload();
    } catch { setCreateError('Error de conexión'); }
    finally { setCreateLoading(false); }
  };

  const closeCreateModal = () => {
    setCreateOpen(false);
    setCreateStep('kind');
    setCreateError('');
    setCreateLoading(false);
    setWaCode('');
    waCodeRef.current = '';
    setCreateForm({ number: '', url: '', type: 'whatsapp', type_handler: 'flow', id_manejador: '' });
    setWebFields([]);
    setWebWelcome('');
    setMetaPages([]);
    setMetaSessionId('');
    setConnectedChannel(null);
    window.removeEventListener('message', handleMetaMessage);
  };

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  return (
    <main style={{ padding: '2rem 1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', boxShadow: `0 4px 24px ${theme.colors.glowSubtle}` }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ color: theme.colors.primary, fontSize: '1.5rem', marginBottom: '0.3rem' }}>Canales</h1>
            <p style={{ color: theme.colors.textMuted, fontSize: '0.9rem' }}>Canales de WhatsApp conectados a tu empresa.</p>
          </div>
          <button
            onClick={() => setCreateOpen(true)}
            style={{ padding: '9px 20px', backgroundColor: theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.9rem', fontWeight: '700', cursor: 'pointer' }}
          >
            + Crear
          </button>
        </div>

        {successMsg && (
          <div style={{ backgroundColor: 'rgba(74,222,128,0.1)', border: `1px solid ${theme.colors.success}`, borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.colors.success, fontSize: '0.9rem' }}>
            {successMsg}
          </div>
        )}

        <DataTable
          ref={tableRef}
          columns={COLUMNS}
          fetchUrl="/api/channels"
          token={token}
          renderActions={(row) => (
            <button
              onClick={() => openEdit(row)}
              style={{ padding: '5px 14px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.primary}`, borderRadius: '6px', color: theme.colors.primary, fontSize: '0.82rem', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              Editar
            </button>
          )}
        />
      </div>

      {/* Modal crear */}
      {createOpen && (
        <div onClick={closeCreateModal} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>
                {createStep === 'kind' ? 'Nuevo Canal'
                  : createStep === 'type' ? 'Nuevo Canal'
                  : createStep === 'intro' ? 'Conectar WhatsApp'
                  : createStep === 'connectMeta' ? 'Conectar con Facebook'
                  : createStep === 'selectPage' ? 'Elegir página'
                  : createStep === 'handler' ? 'Configurar manejador'
                  : createStep === 'web' ? 'Configurar Canal Web'
                  : 'Datos del Canal'}
              </h2>
              <button onClick={closeCreateModal} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            {createStep === 'kind' ? (
              <ChannelKindPicker
                onPickWeb={() => { setCreateForm((f) => ({ ...f, type: 'web' })); setCreateError(''); setCreateStep('web'); }}
                onPickOthers={() => { setCreateError(''); setCreateStep('type'); }}
                onClose={closeCreateModal}
              />
            ) : createStep === 'web' ? (
              <div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Nombre del canal</label>
                  <input value={createForm.number} onChange={(e) => setCreateForm((f) => ({ ...f, number: e.target.value }))} placeholder="Ej: Chat web principal" style={inputStyle} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Mensaje de bienvenida <span style={{ color: theme.colors.textMuted }}>(opcional)</span></label>
                  <input value={webWelcome} onChange={(e) => setWebWelcome(e.target.value)} placeholder="¡Hola! Déjanos tus datos para atenderte." style={inputStyle} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <WebFieldsEditor fields={webFields} setFields={setWebFields} />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={labelStyle}>Tipo de Manejador</label>
                  <select value={createForm.type_handler} onChange={(e) => setCreateForm((f) => ({ ...f, type_handler: e.target.value, id_manejador: '' }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="flow">Flow</option>
                    <option value="campaign">Campaign</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={labelStyle}>{createForm.type_handler === 'campaign' ? 'Campaña' : 'Flujo'} que maneja el canal</label>
                  <select value={createForm.id_manejador} onChange={(e) => setCreateForm((f) => ({ ...f, id_manejador: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">— Seleccionar —</option>
                    {(createForm.type_handler === 'campaign' ? campaigns : flows).map((o) => <option key={o.id} value={o.id}>{o.name} (#{o.id})</option>)}
                  </select>
                </div>
                {createError && <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{createError}</p>}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={() => { setCreateStep('kind'); setCreateError(''); }} disabled={createLoading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>Atrás</button>
                  <button onClick={handleCreateWeb} disabled={createLoading} style={{ flex: 1, padding: '10px', backgroundColor: createLoading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: 700, cursor: createLoading ? 'not-allowed' : 'pointer' }}>
                    {createLoading ? 'Creando...' : 'Crear canal'}
                  </button>
                </div>
              </div>
            ) : createStep === 'type' ? (
              <ChannelTypePicker onPick={handlePickType} onClose={closeCreateModal} />
            ) : createStep === 'intro' ? (
              <OnboardingIntro onStart={handleStartMeta} onClose={closeCreateModal} loading={createLoading} error={createError} />
            ) : createStep === 'connectMeta' ? (
              <ConnectMetaIntro type={createForm.type} onConnect={handleConnectMeta} onClose={closeCreateModal} loading={createLoading} error={createError} />
            ) : createStep === 'selectPage' ? (
              <SelectPage pages={metaPages} onSelect={handleSelectPage} onClose={closeCreateModal} loading={createLoading} error={createError} />
            ) : createStep === 'handler' ? (
              <HandlerStep form={createForm} setForm={setCreateForm} onSubmit={handleConfigureHandler} onClose={closeCreateModal} loading={createLoading} error={createError} flows={flows} campaigns={campaigns} connectedName={connectedChannel?.name} />
            ) : null}
          </div>
        </div>
      )}

      {/* Modal editar */}
      {editOpen && (
        <div onClick={() => !editLoading && setEditOpen(false)} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: theme.colors.surface, borderRadius: '14px', border: `1px solid ${theme.colors.border}`, padding: '2rem', width: '100%', maxWidth: editChannel?.type === 'web' ? '480px' : '380px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem' }}>Editar Canal #{editChannel?.id}</h2>
              <button onClick={() => !editLoading && setEditOpen(false)} style={{ background: 'none', border: 'none', color: theme.colors.textMuted, fontSize: '1.3rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ marginBottom: '1.1rem' }}>
              <label style={labelStyle}>Tipo de Manejador</label>
              <select value={editForm.type_handler} onChange={(e) => setEditForm(f => ({ ...f, type_handler: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="flow">Flow</option>
                <option value="campaign">Campaign</option>
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>ID Manejador</label>
              <select value={editForm.id_manejador} onChange={(e) => setEditForm(f => ({ ...f, id_manejador: e.target.value }))} style={{ ...inputStyle, cursor: 'pointer' }}>
                <option value="">— Seleccionar {editForm.type_handler === 'campaign' ? 'campaña' : 'flujo'} —</option>
                {(editForm.type_handler === 'campaign' ? campaigns : flows).map((o) => (
                  <option key={o.id} value={o.id}>{o.name} (#{o.id})</option>
                ))}
              </select>
            </div>

            {editChannel?.type === 'web' && (
              <>
                <div style={{ marginBottom: '1.1rem' }}>
                  <label style={labelStyle}>Mensaje de bienvenida <span style={{ color: theme.colors.textMuted }}>(opcional)</span></label>
                  <input value={editWebWelcome} onChange={(e) => setEditWebWelcome(e.target.value)} style={inputStyle} />
                </div>
                <div style={{ marginBottom: '1.3rem' }}>
                  <WebFieldsEditor fields={editWebFields} setFields={setEditWebFields} />
                </div>
                <div style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: '1rem', marginBottom: '1rem' }}>
                  <p style={{ color: theme.colors.text, fontSize: '0.85rem', marginBottom: '0.8rem' }}>
                    Para embeber el botón flotante en tu página web, pega este código:
                  </p>
                  <CopyBlock
                    label="Botón flotante (recomendado)"
                    code={`<script\n  src="${window.location.origin}/widget.js"\n  data-company="${editChannel.company_id}"\n  data-channel="${editChannel.id}">\n</script>`}
                  />
                  <CopyBlock
                    label="O incrustar fijo (iframe)"
                    code={`<iframe\n  src="${window.location.origin}/iframe/${editChannel.company_id}/${editChannel.id}"\n  width="100%"\n  height="600"\n  frameborder="0"\n  allow="microphone">\n</iframe>`}
                  />
                </div>
              </>
            )}

            {editError && (
              <p style={{ color: theme.colors.error, fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'center' }}>{editError}</p>
            )}

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button onClick={() => setEditOpen(false)} disabled={editLoading} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.textMuted, fontSize: '0.9rem', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={handleEditSubmit} disabled={editLoading} style={{ flex: 1, padding: '10px', backgroundColor: editLoading ? theme.colors.border : theme.colors.primary, border: 'none', borderRadius: '7px', color: theme.colors.background, fontSize: '0.9rem', fontWeight: '700', cursor: editLoading ? 'not-allowed' : 'pointer' }}>
                {editLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default Channels;
